import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const CC_STATUS_STYLE = {
  extracting: "bg-sky-100 text-sky-800",
  needs_review: "bg-amber-100 text-amber-800",
  validated: "bg-emerald-100 text-emerald-800",
  blocked: "bg-red-100 text-red-700",
  failed: "bg-red-100 text-red-700",
  superseded: "bg-slate-100 text-slate-500",
  open: "bg-emerald-100 text-emerald-800",
  locked: "bg-indigo-100 text-indigo-800",
};

export const CC_STATUS_LABEL = {
  extracting: "extracting…",
  needs_review: "needs review",
  validated: "validated",
  blocked: "blocked",
  failed: "failed",
  superseded: "superseded",
};

export function CCStatusBadge({ status }) {
  return <Badge className={CC_STATUS_STYLE[status] || "bg-slate-100 text-slate-600"} data-testid={`cc-status-${status}`}>{CC_STATUS_LABEL[status] || status}</Badge>;
}

export function fmtAmt(s, { signColor = true } = {}) {
  if (s === null || s === undefined || s === "") return <span className="text-slate-400">—</span>;
  const n = Number(s);
  const txt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
  if (!signColor) return txt;
  return <span className={n < 0 ? "text-red-600" : "text-slate-900"}>{txt}</span>;
}

const TABS = [
  { to: "/commissions", label: "Overview", end: true },
  { to: "/commissions/imports", label: "Imports" },
  { to: "/commissions/matching", label: "Matching" },
  { to: "/commissions/policies", label: "Policies" },
  { to: "/commissions/producers", label: "Producers" },
  { to: "/commissions/rules", label: "Rules" },
  { to: "/commissions/ledger", label: "Ledger" },
  { to: "/commissions/payroll", label: "Payroll" },
];

export const MATCH_STATUS_STYLE = {
  verified_deterministic: "bg-emerald-100 text-emerald-800",
  manual_approved: "bg-teal-100 text-teal-800",
  direct_assigned: "bg-fuchsia-100 text-fuchsia-800",
  candidate: "bg-amber-100 text-amber-800",
  ambiguous: "bg-orange-100 text-orange-800",
  conflict: "bg-red-100 text-red-700",
  unmatched: "bg-slate-100 text-slate-600",
};

export const MATCH_STATUS_LABEL = {
  verified_deterministic: "verified",
  manual_approved: "manual",
  direct_assigned: "assigned",
  candidate: "candidate",
  ambiguous: "ambiguous",
  conflict: "conflict",
  unmatched: "unmatched",
};

export function CCNav() {
  return (
    <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="cc-nav">
      {TABS.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} data-testid={`cc-nav-${t.label.toLowerCase()}`}
          className={({ isActive }) =>
            `px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              isActive ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

export function usePeriods(searchKey) {
  const [periods, setPeriods] = useState([]);
  const [selected, setSelected] = useState(searchKey || "");
  const load = async () => {
    const { data } = await api.get("/commission/periods");
    setPeriods(data);
    if (!selected && data.length) setSelected(data[0].period_key);
    return data;
  };
  useEffect(() => { load().catch(() => {}); }, []);
  return { periods, selected, setSelected, reload: load };
}

export function PeriodBar({ periods, selected, setSelected, onCreated }) {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission/periods", { period_key: val });
      toast.success(`Period ${data.period_key} opened`);
      setOpen(false);
      setVal("");
      onCreated?.(data.period_key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create period");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger className="w-44" data-testid="cc-period-select"><SelectValue placeholder="Select period" /></SelectTrigger>
        <SelectContent>
          {periods.map((p) => (
            <SelectItem key={p.period_key} value={p.period_key}>
              {p.period_key} {p.status !== "open" ? `(${p.status})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasPerm("commission.import") && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="cc-new-period-btn">
          <Plus size={15} className="mr-1" /> New period
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Open a monthly period</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Commission work is organized by month. Statements and EZLynx snapshots are uploaded into a period.</p>
          <Input type="month" value={val} onChange={(e) => setVal(e.target.value)} data-testid="cc-period-input" />
          <Button onClick={create} disabled={!val || busy} data-testid="cc-period-create-btn">Open period</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
